import { useEffect, useMemo } from "react";
import { useWatch, type UseFormReturn } from "react-hook-form";
import moment from "moment-timezone";
import { isEqual } from "lodash";
import { useScheduleContext } from "../context/useScheduleContext";
import { useFormValues } from "./useFormValues";
import { getSectionsBounds } from "../../helpers/form/sections";
import { combineDateAndTime } from "../../../../../utils/helpers/date";

const setIfChanged = (form: UseFormReturn<any>, name: string, value?: Date) => {
    if (value && !moment(form.getValues()[name]).isSame(value, "seconds")) {
        form.setValue(name, value);
    }
};

/** Keeps the form in sync with the selected cell/event and with the edited sections. */
export const useFormSync = ({
    form,
    initialValues,
    isSimple
}: { form: UseFormReturn<any>, initialValues: any, isSimple: boolean }) => {
    const { selectedCellOrEvent } = useScheduleContext();
    const formValues = useFormValues(form);
    const isCreating = selectedCellOrEvent?.isCreating;

    // Re-seed the form when another event is selected, otherwise realign its dates/sections.
    useEffect(() => {
        const currentValues = form.getValues();
        if (isEqual(currentValues, formValues)) {
            return;
        }

        if (currentValues?.id !== formValues?.id || currentValues?.date !== formValues?.date) {
            form.reset(formValues);
            return;
        }

        const sections = formValues?.sections;
        const bounds = getSectionsBounds(sections);
        const start = bounds.start || formValues?.startDate;
        const end = bounds.end || formValues?.endDate;

        setIfChanged(form, "startDate", start ? moment(start).toDate() : undefined);
        setIfChanged(form, "endDate", end ? moment(end).toDate() : undefined);
        if (!isEqual(currentValues.sections, sections)) {
            form.setValue("sections", sections);
        }
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [selectedCellOrEvent?.id, selectedCellOrEvent?.date, form]);

    // Fill in the defaults a new event inherits once they resolve.
    const { payCode, job, location } = initialValues ?? {};
    useEffect(() => {
        if (!isCreating) {
            return;
        }

        const currentValues = form.getValues();
        Object.entries({ payCode, job, location }).forEach(([name, value]) => {
            if (!currentValues?.[name] && value) {
                form.setValue(name, value);
            }
        });
    }, [isCreating, payCode, job, location, form]);

    const sections = useWatch({ control: form.control, name: "sections" });
    const shiftDate = useWatch({ control: form.control, name: "date" });

    // Bounds of the advanced sections, anchored on the shift date.
    const { start: sectionsStart, end: sectionsEnd } = useMemo(() => {
        if (isSimple || !sections?.length) {
            return {} as { start?: Date, end?: Date };
        }

        const anchor = moment(typeof shiftDate === "string" ? undefined : shiftDate);
        const normalised = sections
            .filter((section: any) => section?.start && section?.end)
            .map((section: any) => {
                const start = combineDateAndTime(anchor, moment(section.start));
                let end = combineDateAndTime(anchor, moment(section.end));
                if (moment(end).isBefore(start)) {
                    end = moment(end).add(1, "day");
                }
                return { ...section, start, end };
            });

        return getSectionsBounds(normalised);
    }, [isSimple, sections, shiftDate]);

    const sectionsStartTime = sectionsStart?.getTime();
    const sectionsEndTime = sectionsEnd?.getTime();

    // Mirror the edited sections onto the shift start/end.
    useEffect(() => {
        if (isSimple || isCreating) {
            return;
        }

        const currentValues = form.getValues();
        if (sectionsStartTime && !moment(currentValues.startDate).isSame(sectionsStartTime)) {
            form.setValue("startDate", new Date(sectionsStartTime));
        }
        if (sectionsEndTime && !moment(currentValues.endDate).isSame(sectionsEndTime)) {
            form.setValue("endDate", new Date(sectionsEndTime));
        }
    }, [isSimple, isCreating, sectionsStartTime, sectionsEndTime, form]);
};
